/**
 * @format
 */

import { SalesSummary, TrendData } from '../types/dashboard.types';

/**
 * Calculate percentage change between two values
 */
export const calculatePercentageChange = (previous: number, current: number): number => {
  if (previous === 0) return current === 0 ? 0 : 100;
  if (!previous || !current) return 0;
  return ((current - previous) / previous) * 100;
};

/**
 * Calculate trend data including value and percentage change
 */
export const calculateTrend = (previous: number, current: number): TrendData => {
  const trend = calculatePercentageChange(previous, current);
  return {
    value: current,
    trend,
    isPositive: trend >= 0,
  };
};

/**
 * Calculate All Trends from Sales Summary
 */
export const calculateAllTrends = (summary: SalesSummary) => {
  const totalSpentTrend = calculateTrend(
    summary.previousCost || 0,
    summary.totalCost
  );

  const totalSalesTrend = calculateTrend(
    (summary.previousU65Sales || 0) + (summary.previousACASales || 0),
    summary.totalPrivateMed + summary.totalMajorMed
  );

  const majorMedTrend = calculateTrend(
    summary.previousACASales || 0,
    summary.totalMajorMed
  );

  const privateMedTrend = calculateTrend(
    summary.previousU65Sales || 0,
    summary.totalPrivateMed
  );

  const ancillariesTrend = calculateTrend(
    summary.previousAncillaries || 0,
    summary.totalAncillaries
  );

  const leadsTrend = calculateTrend(
    summary.previousBillableLeads || 0,
    summary.totalBillableLeads
  );

  const totalLeadsTrend = calculateTrend(
    summary.previousLeads || 0,
    summary.totalLeads
  );

  return {
    totalSpentTrend,
    totalSalesTrend,
    majorMedTrend,
    privateMedTrend,
    ancillariesTrend,
    leadsTrend,
    totalLeadsTrend,
  };
};

/**
 * Format number as local string
 */
export const formatLocalString = (amount: number | undefined): string => {
  return amount ? amount.toLocaleString() : '0';
};

/**
 * Format currency with dollar sign
 */
export const formatCurrencyDisplay = (amount: number | undefined): string => {
  return `$${formatLocalString(amount)}`;
};

/**
 * Get color based on performance percentage
 */
export const getPerformanceColor = (percentage: number): string => {
  if (percentage >= 0.75) {
    return 'green'; // Green for 75% and above
  } else if (percentage >= 0.65 && percentage <= 0.74) {
    return '#FFDB58'; // Yellow for 65-74%
  } else {
    return 'red'; // Red for below 65%
  }
};

/**
 * Format PTC value
 */
export const formatPTCValue = (value: number): string => {
  return `${value.toFixed(2)}x`;
};
